import React from 'react'
import styled from '@emotion/styled'
import {
  CheckboxBase,
  LegalIcon,
  MarkerIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  Text,
  spacing,
} from '@instacart/ids-core'
import { useDomainMessage, type DomainMessages } from 'domains/account-management/utils/domain/intl'
import { OptionType } from 'domains/account-management/components/retailer-location-store-group-select/utils/types'

const GroupHeaderContainer = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  height: '38px',
  padding: `0 ${spacing.s32}px 0 ${spacing.s24}px`,
  borderBottom: `1px solid ${theme.colors.systemGrayscale20}`,
}))

const TypeIconContainer = styled.div({
  display: 'flex',
  marginLeft: spacing.s8,
})

const ExpandedContainer = styled.div({
  marginLeft: 'auto',
})

const headerTypeMapping: Record<OptionType, { messageId: DomainMessages; icon: JSX.Element }> = {
  [OptionType.StoreGroup]: {
    messageId: 'accountManagementDomain.form.addStore.allStoreGroups',
    icon: <LegalIcon color="systemGrayscale30" size={20} />,
  },
  [OptionType.RetailerLocation]: {
    messageId: 'accountManagementDomain.form.addStore.allRetailerLocations',
    icon: <MarkerIcon color="systemGrayscale30" size={20} />,
  },
}

interface GroupHeadingProps {
  type: OptionType
  count: number
  checked: boolean | 'indeterminate'
  expanded: boolean
  onClick: () => void
  onClickCheckbox: () => void
}

export const GroupHeading = ({
  type,
  count = 0,
  expanded,
  checked,
  onClick,
  onClickCheckbox,
}: GroupHeadingProps) => {
  const { messageId, icon } = headerTypeMapping[type]

  const headerMessage = useDomainMessage(messageId, { number: count })

  const expandedIcon = expanded ? (
    <ChevronDownIcon color="systemGrayscale30" size={20} />
  ) : (
    <ChevronUpIcon color="systemGrayscale30" size={20} />
  )

  const checkboxId = `group-checkbox-${type}`
  return (
    <GroupHeaderContainer onClick={onClick}>
      <CheckboxBase
        id={checkboxId}
        checked={checked}
        onClick={e => {
          e?.stopPropagation()
          onClickCheckbox?.()
        }}
      />
      <Text typography="bodyMedium1" color="systemGrayscale70">
        {headerMessage}
      </Text>
      <TypeIconContainer>{icon}</TypeIconContainer>
      <ExpandedContainer>{expandedIcon}</ExpandedContainer>
    </GroupHeaderContainer>
  )
}
